import { useEffect, useMemo, useRef, useState } from 'react';
import common from '../styles/common.module.css';
import styles from '../styles/Battlefield.module.css';
import useFocusTrap from '../hooks/useFocusTrap.js';
import { groupByTeam } from '../engine/roster/team';
import BattlefieldModal from './BattlefieldModal';

/** A team header's checkbox: all, none, or some - `indeterminate` is a property, not an attribute. */
function TeamCheck({ label, members, travellers, onToggle, tag }) {
  const ref = useRef(null);
  const ticked = members.filter((m) => travellers.has(m.id)).length;
  const all = members.length > 0 && ticked === members.length;
  const some = ticked > 0 && !all;
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = some;
  }, [some]);
  return (
    <label className={styles.cutTeam}>
      <input
        ref={ref}
        type="checkbox"
        checked={all}
        data-testid={`cut-team-${tag}`}
        onChange={() => onToggle(members, !all)}
      />
      <span>{label}</span>
    </label>
  );
}

/** A new scene's name and map - the step NEW SCENE puts in front of the travellers. */
function NewSceneFields({ name, setName, mapKey, setMapKey, maps }) {
  return (
    <div className={styles.cutFields}>
      <label className={common.label}>
        Name
        <input
          className={common.input}
          data-testid="cut-name"
          // eslint-disable-next-line jsx-a11y/no-autofocus -- the new-scene form opened for this one name
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="The docks"
        />
      </label>
      <label className={common.label}>
        Map
        <select
          className={common.input}
          data-testid="cut-map"
          value={mapKey}
          onChange={(e) => setMapKey(e.target.value)}
        >
          {maps.map((m) => (
            <option key={m.key} value={m.key}>
              {m.label}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}

/** One team's travellers: a tri-state header when there is more than one team, a box per row. */
function TravellerGroup({ group, showHeader, travellers, toggleMany }) {
  const tag = group.team?.tag ?? 'none';
  return (
    <div className={styles.cutGroup} data-livery={group.livery ?? undefined}>
      {showHeader && (
        <TeamCheck
          label={group.team?.label || group.team?.tag || 'No team'}
          members={group.members}
          travellers={travellers}
          onToggle={toggleMany}
          tag={tag}
        />
      )}
      {group.members.map((c) => (
        <label key={c.id} className={styles.cutRow}>
          <input
            type="checkbox"
            data-testid={`cut-traveller-${c.id}`}
            checked={travellers.has(c.id)}
            onChange={(e) => toggleMany([c], e.target.checked)}
          />
          <span>{c.name}</span>
        </label>
      ))}
    </div>
  );
}

/** Who comes along, and the ticking of them - one at a time or a whole team. */
function useTravellers(defaults) {
  const [travellers, setTravellers] = useState(() => new Set(defaults));
  const toggleMany = (members, on) =>
    setTravellers((prev) => {
      const next = new Set(prev);
      for (const m of members)
        if (on) next.add(m.id);
        else next.delete(m.id);
      return next;
    });
  return { travellers, toggleMany };
}

/** Cancel, and the confirm - or the refusal IN PLACE OF it. */
function CutActions({ why, incomplete, kind, onClose, onConfirm }) {
  return (
    <div className={styles.cutActions}>
      <button type="button" className={common.btnSecondary} onClick={() => onClose?.()}>
        Cancel
      </button>
      {why ? (
        <span className={styles.cutRefusal} role="status" data-testid="cut-refusal">
          {why}
        </span>
      ) : (
        <button
          type="button"
          className={common.btnPrimary}
          data-testid="cut-confirm"
          disabled={incomplete}
          onClick={onConfirm}
        >
          {kind === 'new' ? 'Raise the stage' : 'Cut'}
        </button>
      )}
    </div>
  );
}

/**
 * Who comes along, and where the stage is parked.
 *
 * The one dialog behind CUT TO and NEW SCENE: NEW SCENE puts a map step
 * in front of the same travellers list. The roster is grouped by team
 * the way the gallery groups it (`groupByTeam`), a tri-state box per
 * header, one per row. `refusal(travellers)` is the engine's sentence,
 * shown IN PLACE OF the button - a cut that cannot happen has no button
 * to press (v8.338.0).
 */
export default function SceneCutDialog(props) {
  const { kind = 'cut', targetName = '', roster = [], refusal = () => null, needsParkName = false, maps = [] } = props;
  const { travellers, toggleMany } = useTravellers(props.defaults ?? new Set());
  const [parkAs, setParkAs] = useState(props.suggestedParkName ?? '');
  const [name, setName] = useState('');
  const [mapKey, setMapKey] = useState(maps[0]?.key ?? '');
  const trapRef = useFocusTrap(true);
  const groups = useMemo(() => groupByTeam(roster, null), [roster]);
  const title = kind === 'new' ? 'New scene' : `Cut to ${targetName}`;
  const confirm = () => props.onConfirm?.({ travellers, parkAs: parkAs.trim(), name: name.trim(), mapKey });
  return (
    <BattlefieldModal
      onBackdropClick={() => props.onClose?.()}
      trapRef={trapRef}
      style={{ maxWidth: 420 }}
      label={title}
      testId="cut-dialog"
    >
      <h3 className={styles.cutTitle}>{title}</h3>
      {kind === 'new' && <NewSceneFields {...{ name, setName, mapKey, setMapKey, maps }} />}
      <div className={styles.cutSection}>
        <div className={styles.cutSectionHead}>Who comes along</div>
        {roster.length === 0 && <div className={styles.roomEmpty}>Nobody is on stage.</div>}
        {groups.map((g, gi) => (
          <TravellerGroup
            key={(g.team?.tag ?? 'none') + gi}
            group={g}
            showHeader={groups.length > 1}
            travellers={travellers}
            toggleMany={toggleMany}
          />
        ))}
      </div>
      {needsParkName && (
        <label className={common.label}>
          Park the stage as
          <input
            className={common.input}
            data-testid="cut-park-as"
            value={parkAs}
            onChange={(e) => setParkAs(e.target.value)}
          />
        </label>
      )}
      <CutActions
        why={refusal(travellers)}
        incomplete={kind === 'new' && (!name.trim() || !mapKey)}
        kind={kind}
        onClose={props.onClose}
        onConfirm={confirm}
      />
    </BattlefieldModal>
  );
}
